import { motion } from 'framer-motion';
import { Inbox, Star, Mail, Clock, ExternalLink } from 'lucide-react';

const otherEmails = [
  { from: 'HR Department', subject: 'Q1 Performance Reviews', time: '10:30 AM', read: true },
  { from: 'IT Support', subject: 'System Maintenance Notice', time: '9:15 AM', read: true },
];

export default function EmailInboxScene({ onNext }) {
  return (
    <div data-testid="scene-email-inbox" className="flex flex-col items-center justify-center h-full px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full max-w-3xl"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-4">
            <Inbox size={14} /> Step 4
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
            Recipient Opens Email
          </h2>
          <p className="mt-3 font-body text-lg text-[#737373]">
            User receives the invite and clicks the link
          </p>
        </motion.div>

        {/* Email client mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="bg-white rounded-2xl border border-black/5 shadow-2xl overflow-hidden"
        >
          {/* Email toolbar */}
          <div className="flex items-center gap-3 px-5 py-3 border-b border-[#E5E7EB]">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
              <div className="w-3 h-3 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="px-4 py-1 rounded-md bg-[#FAFAFA] text-xs font-body text-[#737373]">
                mail.alnoor.ae
              </div>
            </div>
          </div>

          {/* Sidebar + email list */}
          <div className="flex">
            {/* Mini sidebar */}
            <div className="w-14 bg-[#FAFAFA] border-r border-[#E5E7EB] py-4 flex flex-col items-center gap-4">
              <Inbox size={18} className="text-klein" />
              <Star size={18} className="text-[#737373]" />
              <Mail size={18} className="text-[#737373]" />
              <Clock size={18} className="text-[#737373]" />
            </div>

            {/* Email list */}
            <div className="flex-1">
              {/* Shafafiya invite email - highlighted */}
              <motion.div
                initial={{ backgroundColor: '#FFFFFF' }}
                animate={{ backgroundColor: ['#FFFFFF', '#E6F5F3', '#E6F5F3'] }}
                transition={{ delay: 0.8, duration: 1 }}
                className="px-5 py-4 border-b border-[#E5E7EB] cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6, type: 'spring' }}
                    className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5"
                  >
                    <span className="text-white text-xs font-bold">S</span>
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-heading font-bold text-sm text-[#0A0A0A]">Shafafiya Portal</p>
                      <span className="text-xs font-body text-teal-700 font-medium">Just now</span>
                    </div>
                    <p className="font-body font-medium text-sm text-[#0A0A0A] mt-0.5">
                      You have been invited to join Shafafiya
                    </p>
                    <p className="font-body text-xs text-[#737373] mt-1 line-clamp-1">
                      Dear Ahmed, you have been invited to access the Shafafiya portal as Organization Owner for Al Noor Healthcare Group...
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Other emails - dimmed */}
              {otherEmails.map((email, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="px-5 py-4 border-b border-[#E5E7EB]"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#E5E7EB] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#737373] text-xs font-bold">{email.from[0]}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-body text-sm text-[#737373]">{email.from}</p>
                        <span className="text-xs font-body text-[#737373]">{email.time}</span>
                      </div>
                      <p className="font-body text-sm text-[#737373] mt-0.5">{email.subject}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Click indicator */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0 }}
          className="mt-6 flex items-center justify-center gap-2"
        >
          <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ delay: 2.2, duration: 1, repeat: Infinity }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 text-white font-body font-medium text-sm shadow-lg shadow-teal-600/25 cursor-pointer"
            onClick={onNext}
          >
            <ExternalLink size={16} />
            Accept Invite
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
